import React from 'react';
import {
  Box,
  HStack,
  Text,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

type QuestionProgressStripProps = {
  isRecording: boolean;
  /** Server is restarting LiveKit egress; do not show a live-recording chip. */
  isRecordingRestarting?: boolean;
  isConnected: boolean;
  currentQuestionIndex?: number | null;
  totalQuestions: number;
  /** When false, recording/connection remain but question counter + dots are hidden (R3 pre-commit). */
  showQuestionProgress?: boolean;
};

/** Top status strip: recording + connection indicators and question progress dots. */
export function QuestionProgressStrip({
  isRecording,
  isRecordingRestarting = false,
  isConnected,
  currentQuestionIndex,
  totalQuestions,
  showQuestionProgress = true,
}: QuestionProgressStripProps) {
  const { t } = useTranslation();

  if (totalQuestions <= 0) return null;

  const resolvedIndex = currentQuestionIndex ?? 0;
  const currentDisplay = resolvedIndex + 1;
  const filledCount = Math.min(Math.max(resolvedIndex + 1, 0), totalQuestions);

  return (
    <HStack
      testID="oral-exam-question-progress"
      alignItems="flex-start"
      width="100%"
      py="$1"
    >
      <HStack flex={1} space="sm" alignItems="center">
        {isRecordingRestarting ? (
          <HStack
            space="xs"
            alignItems="center"
            testID="oral-exam-rec-restarting-indicator"
            accessibilityValue={{ text: 'recording-restarting' }}
          >
            <Box w={8} h={8} borderRadius="$full" bg="$statusWarning" />
            <Text size="xs" color="$textBody" fontWeight="medium">
              {t('oralExam.statusRecordingRestarting', {
                defaultValue: 'Restarting recording',
              })}
            </Text>
          </HStack>
        ) : null}

        {isRecording ? (
          <HStack
            space="xs"
            alignItems="center"
            testID="oral-exam-rec-indicator"
            accessibilityValue={{ text: 'recording' }}
          >
            <Box w={8} h={8} borderRadius="$full" bg="$statusError" />
            <Text size="xs" color="$textBody" fontWeight="medium">
              {t('oralExam.statusRecording')}
            </Text>
          </HStack>
        ) : null}

        {isRecording || isRecordingRestarting ? (
          <Text size="xs" color="$borderLight" fontWeight="light">|</Text>
        ) : null}

        <HStack
          space="xs"
          alignItems="center"
          testID="oral-exam-connection-indicator"
          accessibilityValue={{ text: isConnected ? 'connected' : 'disconnected' }}
        >
          <Box
            w={8}
            h={8}
            borderRadius="$full"
            bg={isConnected ? '$statusSuccess' : '$textDisabled'}
          />
          <Text size="xs" color="$textBody" fontWeight="medium">
            {isConnected
              ? t('oralExam.statusConnected')
              : t('oralExam.reconnect.title', { defaultValue: 'Reconnecting…' })}
          </Text>
        </HStack>
      </HStack>

      <HStack alignItems="center" space="sm" testID="oral-exam-question-progress-center">
        {showQuestionProgress ? (
          <>
            <Text size="sm" color="$textHeading" fontWeight="semibold">
              {t('oralExam.questionProgress', {
                current: currentDisplay,
                total: totalQuestions,
              })}
            </Text>
            <HStack
              space="xs"
              alignItems="center"
              testID="oral-exam-question-progress-dots"
            >
              {Array.from({ length: totalQuestions }, (_, index) => (
                <Box
                  key={`question-progress-dot-${index}`}
                  w={8}
                  h={8}
                  borderRadius="$full"
                  bg={index < filledCount ? '$brandBase' : '$borderLight'}
                />
              ))}
            </HStack>
          </>
        ) : null}
      </HStack>

      <Box flex={1} />
    </HStack>
  );
}
